import React, { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  PaperPlaneTilt,
  VideoCamera,
  CalendarPlus,
  Spinner,
  LockKey,
  X,
  Microphone,
  CheckCircle,
  StopCircle,
  Trash,
  PaperPlane,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import useMediaRecorder from "../hooks/useMediaRecorder";
import Disclaimer from "../components/Disclaimer";
import { useAuth } from "../context/AuthContext";

function fmtTime(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

// ——— Inline video recorder (same UX pattern as the thread composer) ———
function VideoRecorderPanel({ onSend, onCancel }) {
  const { recording, elapsed, stream, start, stop } = useMediaRecorder({ video: true });
  const [blob, setBlob] = useState(null);
  const [duration, setDuration] = useState(0);
  const [sending, setSending] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    start();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (videoRef.current && stream) videoRef.current.srcObject = stream;
  }, [stream]);

  const handleStop = async () => {
    setDuration(elapsed);
    const b = await stop();
    setBlob(b);
  };

  const send = async () => {
    if (!blob) return;
    setSending(true);
    try {
      await onSend(blob, duration);
    } finally {
      setSending(false);
    }
  };

  const videoUrl = blob ? URL.createObjectURL(blob) : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8E4DB]">
          <div className="font-serif text-lg text-[#1E3A2B]">
            {recording ? "Recording video…" : blob ? "Review video" : "Preparing camera…"}
          </div>
          <button onClick={onCancel} className="text-[#8A948E] hover:text-[#1E3A2B]" aria-label="Close">
            <X size={20} weight="bold" />
          </button>
        </div>
        <div className="p-5">
          {!blob ? (
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              className="w-full rounded-xl bg-black aspect-video"
              data-testid="coach-video-recorder-preview"
            />
          ) : (
            <video
              src={videoUrl}
              controls
              playsInline
              className="w-full rounded-xl bg-black aspect-video"
              data-testid="coach-video-recorder-playback"
            />
          )}
          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
            {recording ? (
              <>
                <div className="flex items-center gap-2 text-[#C77D5E]">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#C77D5E] animate-pulse" />
                  <span className="font-mono text-[#1E3A2B]">{fmtTime(elapsed)}</span>
                </div>
                <button
                  onClick={handleStop}
                  data-testid="coach-video-stop"
                  className="inline-flex items-center gap-2 bg-[#1E3A2B] text-white px-5 py-2.5 rounded-full hover:bg-[#2C4C3B]"
                >
                  <StopCircle size={18} weight="fill" /> Stop
                </button>
              </>
            ) : blob ? (
              <div className="ml-auto flex flex-wrap gap-2">
                <button
                  onClick={onCancel}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#F3F0EA] hover:bg-[#E8E4DB] text-[#121815]"
                >
                  <Trash size={16} weight="duotone" /> Discard
                </button>
                <button
                  onClick={send}
                  disabled={sending}
                  data-testid="coach-video-send"
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#1E3A2B] text-white hover:bg-[#2C4C3B] disabled:opacity-60"
                >
                  {sending ? <Spinner size={16} className="animate-spin" /> : <PaperPlane size={16} weight="fill" />}
                  Send to coach
                </button>
              </div>
            ) : (
              <div className="text-sm text-[#8A948E]">Allow camera access to begin.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ——— Schedule modal ———
function ScheduleModal({ description, onScheduled, onCancel }) {
  const [slots, setSlots] = useState(null); // null=loading, [] = empty
  const [selectedId, setSelectedId] = useState("");
  const [medium, setMedium] = useState("video");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .get("/coach/availability")
      .then(({ data }) => {
        if (!alive) return;
        setSlots(data.slots || []);
      })
      .catch(() => {
        if (alive) setSlots([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    if (!selectedId) {
      toast.error("Select an available time first.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/coach/thread", {
        description: description.trim() || null,
        slot_id: selectedId,
        scheduled_medium: medium,
      });
      const slot = (slots || []).find((s) => s.id === selectedId);
      const when = slot ? new Date(slot.starts_at) : new Date();
      onScheduled(data, when, medium);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not schedule");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/35 flex items-center justify-center p-4" onClick={onCancel}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl p-7 max-w-md w-full border border-[#E8E4DB] shadow-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="label-kicker">Schedule a live conversation</div>
          <button type="button" onClick={onCancel} className="text-[#8A948E] hover:text-[#1E3A2B]" aria-label="Close">
            <X size={18} weight="bold" />
          </button>
        </div>
        <h3 className="font-serif text-2xl text-[#1E3A2B]">Pick from your coach's availability.</h3>
        <p className="text-sm text-[#4A524C] mt-2 leading-relaxed">
          These are the times your coach has set aside. Choose one — both of you join the same private room.
        </p>

        <div className="mt-6">
          <span className="label-kicker block mb-3">Available times</span>
          {slots === null ? (
            <div className="text-[#8A948E] text-sm flex items-center gap-2">
              <Spinner size={14} className="animate-spin" /> Loading times…
            </div>
          ) : slots.length === 0 ? (
            <div
              data-testid="schedule-no-slots"
              className="text-sm text-[#4A524C] bg-[#F3F0EA] rounded-xl px-4 py-5"
            >
              <div className="font-medium text-[#1E3A2B] mb-1">No times published yet.</div>
              Send your coach a text or video message above — they'll publish times shortly.
            </div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1" data-testid="schedule-slot-list">
              {slots.map((s) => {
                const d = new Date(s.starts_at);
                const dateLabel = d.toLocaleDateString(undefined, {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                });
                const timeLabel = d.toLocaleTimeString(undefined, {
                  hour: "numeric",
                  minute: "2-digit",
                });
                const active = selectedId === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setSelectedId(s.id)}
                    data-testid={`schedule-slot-${s.id}`}
                    className={`w-full text-left flex items-center justify-between gap-3 px-4 py-3 rounded-xl border transition-all ${
                      active
                        ? "bg-[#1E3A2B] text-white border-[#1E3A2B]"
                        : "bg-white text-[#121815] border-[#E8E4DB] hover:border-[#1E3A2B]"
                    }`}
                  >
                    <div>
                      <div className="font-medium">{dateLabel}</div>
                      <div className={`text-xs ${active ? "text-white/75" : "text-[#8A948E]"}`}>
                        {timeLabel} · {s.duration_min} min
                      </div>
                    </div>
                    {active && <CheckCircle size={18} weight="duotone" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-5">
          <span className="label-kicker block mb-2">Format</span>
          <div className="flex gap-2">
            {[
              { v: "video", label: "Video", icon: VideoCamera },
              { v: "audio", label: "Audio", icon: Microphone },
            ].map((o) => {
              const Icon = o.icon;
              const active = medium === o.v;
              return (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setMedium(o.v)}
                  data-testid={`schedule-medium-${o.v}`}
                  className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border transition-all ${
                    active
                      ? "bg-[#1E3A2B] text-white border-[#1E3A2B]"
                      : "bg-white text-[#4A524C] border-[#E8E4DB] hover:border-[#1E3A2B]"
                  }`}
                >
                  <Icon size={18} weight="duotone" />
                  {o.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-7 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-5 py-2.5 rounded-xl bg-[#F3F0EA] hover:bg-[#E8E4DB] text-[#121815]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || !selectedId}
            data-testid="schedule-confirm-button"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#1E3A2B] text-white hover:bg-[#2C4C3B] disabled:opacity-60"
          >
            {busy ? <Spinner size={16} className="animate-spin" /> : <CalendarPlus size={16} weight="duotone" />}
            Confirm schedule
          </button>
        </div>
      </form>
    </div>
  );
}

export default function Coach() {
  const nav = useNavigate();
  const loc = useLocation();
  const { leader, coach } = useAuth();
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  // `/coach/my`   = "Contact Your Coach Now" — for leaders with an assigned coach
  // `/coach`      = "Work With a Coach Now"  — for anyone else (or first-time visitors)
  const isMineRoute = loc.pathname.startsWith("/coach/my");

  // Gate: leaders must sign in before opening a thread. A signed-in coach
  // viewing this page is fine (they're the recipient).
  if (!leader && !coach) {
    const nextParam = isMineRoute ? "/coach/my" : "/coach";
    return <Navigate to={`/auth/login?next=${encodeURIComponent(nextParam)}`} replace state={{ from: nextParam }} />;
  }

  // On the "my coach" route, if the leader hasn't been assigned yet, gently
  // redirect them to the "get started" flow so they know what to do.
  const showAssignedFlow = isMineRoute && Boolean(leader?.assigned_coach_id);

  const sendText = async () => {
    if (!description.trim()) {
      toast.error("Type a short message first.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/coach/thread", {
        description: description.trim(),
      });
      nav(`/coach/thread/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open thread");
    } finally {
      setBusy(false);
    }
  };

  const sendVideo = async (blob, durationSec) => {
    // Open a thread (carrying any text the leader typed as the first text message)
    // then upload the recorded video as a second message and jump into the thread.
    try {
      const { data: thread } = await api.post("/coach/thread", {
        description: description.trim() || null,
      });
      const fd = new FormData();
      fd.append("sender", "user");
      fd.append("duration_sec", String(durationSec || 0));
      const ext = blob.type.includes("mp4") ? "mp4" : "webm";
      fd.append("file", blob, `recording.${ext}`);
      await api.post(`/coach/thread/${thread.id}/message`, fd);
      toast.success("Video sent to your coach.");
      nav(`/coach/thread/${thread.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send video");
    }
  };

  const handleScheduled = (data, when, medium) => {
    toast.success(
      `${medium === "video" ? "Video" : "Audio"} call scheduled for ${when.toLocaleString()}`
    );
    nav(`/coach/thread/${data.id}`);
  };

  return (
    <div className="max-w-2xl mx-auto px-6 sm:px-8 py-12 lg:py-20">
      <div className="anim-in delay-1">
        <div className="label-kicker" style={{ color: "#1E3A2B" }}>Concierge Conflict Support</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#1E3A2B] mt-3 tracking-tight leading-tight anim-in delay-2">
          {isMineRoute ? "Contact Your Coach." : "Work with a Coach."}
        </h1>
      </div>

      {/* Path-specific onboarding — /coach = get-started flow (message routed to
          central inbox); /coach/my = talk to your assigned coach. */}
      {!showAssignedFlow ? (
        <div
          data-testid="coach-onboarding-unassigned"
          className="mt-8 bg-[#F3F0EA] border border-[#1E3A2B]/15 rounded-2xl p-6 anim-in delay-3"
        >
          <div className="label-kicker mb-2" style={{ color: "#C77D5E" }}>
            {isMineRoute && !leader?.assigned_coach_id ? "You don't have a coach yet" : "Work with a coach now"}
          </div>
          <h2 className="font-serif text-2xl text-[#1E3A2B] tracking-tight leading-tight">
            Send us the situation and a coach will reach out.
          </h2>
          <p className="mt-3 text-[#4A524C] leading-relaxed">
            Write a short message below, record a voice message, send a video
            or schedule an immediate appointment. A coach will reply to you at
            their earliest convenience. That means minutes or hours — not days.
          </p>
          <p className="mt-3 text-[#4A524C] leading-relaxed">
            If you find the experience valuable, you and the coach can talk
            about continuing further. If not, you'll leave with new perspective
            on a real problem — at no cost and with no obligation.
          </p>
        </div>
      ) : (
        <div
          data-testid="coach-onboarding-assigned"
          className="mt-8 bg-[#F3F0EA] border border-[#1E3A2B]/15 rounded-2xl p-6 anim-in delay-3"
        >
          <div className="label-kicker mb-2" style={{ color: "#1E3A2B" }}>You have a coach</div>
          <h2 className="font-serif text-2xl text-[#1E3A2B] tracking-tight leading-tight">
            Send a message or book a session.
          </h2>
          <ul className="mt-4 space-y-2 text-[#4A524C] leading-relaxed">
            <li className="flex items-start gap-2">
              <span className="text-[#1E3A2B] font-medium">•</span>
              <span>
                <strong className="text-[#1E3A2B]">Send a text, audio, or video</strong> —
                your coach will pick up the thread and respond when they're
                next available.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#1E3A2B] font-medium">•</span>
              <span>
                <strong className="text-[#1E3A2B]">Book a live audio or video call</strong> —
                schedule now for right away or a later date/time.
              </span>
            </li>
          </ul>
        </div>
      )}

      <div className="mt-10 anim-in delay-4">
        <div className="label-kicker mb-3" style={{ color: "#1E3A2B" }}>
          {!showAssignedFlow ? "Describe what's going on" : "Send your coach a note"}
        </div>
        <textarea
          data-testid="coach-description-input"
          rows={7}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="A few sentences is enough. Your coach will pick up the thread from here."
          className="w-full bg-white border border-[#E8E4DB] focus:border-[#1E3A2B] focus:ring-2 focus:ring-[#768C7E]/30 focus:outline-none rounded-2xl p-5 text-[#121815] placeholder-[#8A948E] text-base leading-relaxed resize-none transition-all"
        />

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <button
            onClick={sendText}
            disabled={busy}
            data-testid="coach-send-text-button"
            className="inline-flex items-center justify-center gap-2 bg-[#1E3A2B] text-white px-5 py-3.5 rounded-xl font-medium hover:bg-[#2C4C3B] disabled:opacity-60 transition-colors"
          >
            {busy ? <Spinner size={18} className="animate-spin" /> : <PaperPlaneTilt size={18} weight="fill" />}
            Send text
          </button>
          <button
            onClick={() => setShowVideo(true)}
            disabled={busy}
            data-testid="coach-record-video-button"
            className="inline-flex items-center justify-center gap-2 bg-white text-[#1E3A2B] border border-[#1E3A2B] px-5 py-3.5 rounded-xl font-medium hover:bg-[#F3F0EA] disabled:opacity-60 transition-colors"
          >
            <VideoCamera size={18} weight="duotone" />
            Record a video
          </button>
          <button
            onClick={() => setShowSchedule(true)}
            disabled={busy}
            data-testid="coach-schedule-button"
            className="inline-flex items-center justify-center gap-2 bg-white text-[#1E3A2B] border border-[#1E3A2B] px-5 py-3.5 rounded-xl font-medium hover:bg-[#F3F0EA] disabled:opacity-60 transition-colors"
          >
            <CalendarPlus size={18} weight="duotone" />
            Schedule a conversation
          </button>
        </div>

        <Disclaimer className="mt-6" />
      </div>

      {showSchedule && (
        <ScheduleModal
          description={description}
          onScheduled={handleScheduled}
          onCancel={() => setShowSchedule(false)}
        />
      )}
      {showVideo && (
        <VideoRecorderPanel
          onSend={async (blob, dur) => {
            await sendVideo(blob, dur);
            setShowVideo(false);
          }}
          onCancel={() => setShowVideo(false)}
        />
      )}
    </div>
  );
}
